<!--自定义输入框-->
<script setup>
import Icon from '@/components/common/Icon/index.vue'
import { onClickOutside } from '@vueuse/core'
import { debounce, throttle } from 'lodash-es'
import CustomKeyboard from '@/components/common/CustomKeyboard/index.vue'

defineOptions({
  name: 'CustomInput'
})
const props = defineProps({
  icon: {
    type: String,
    default: ''
  },

  placeholder: {
    type: String,
    default: ''
  },

  // 是否可点击
  disabled: {
    type: Boolean,
    default: false
  },

  // 键盘弹出框位置
  placement: {
    type: String,
    default: 'bottom'
  },

  // 键盘弹出框位置偏移量
  popoverOffset: {
    type: Number,
    default: undefined
  },

  // 大小
  size: {
    type: String,
    default: 'default'
  },

  /**
   * 自动聚焦
   */
  autoFocus: {
    type: Boolean,
    default: false
  },

  /**
   * 点击输入框外是否自动失焦
   */
  autoBlur: {
    type: Boolean,
    default: true
  },

  /**
   * 点击输入框后是否弹出自定义键盘
   */
  showKeyboard: {
    type: Boolean,
    default: true
  },

  clearable: {
    type: Boolean,
    default: false
  },

  popperClass: {
    type: String,
    default: ''
  },
  // 区分密码框和文本框
  type: {
    type: String,
    default: 'text'
  },

  /**
   * 自定义键盘支持的语言
   * [ 'zh', 'en'] // 支持中文和英文
   * ['en'] // 仅支持英文
   * ['zh'] // 仅支持中文
   */
  enableLanguages: {
    type: Array,
    default: () => ['en']
  },

  /**
   * 数字键盘的布局方式
   * horizontal 水平布局
   * vertical 垂直布局
   */
  numberLayout: {
    type: String,
    default: 'horizontal'
  },

  /**
   * 键盘类型
   * [ 'en', 'num'] // 支持中英文和数字键盘
   * ['en'] // 仅英文键盘
   * ['num'] // 仅数字键盘
   */
  keyboardTypes: {
    type: Array,
    default: () => ['en', 'num']
  },

  /**
   * 行数，仅type=textarea有效
   */
  rows: {
    type: Number,
    default: 1
  }
})

const emits = defineEmits(['focus', 'blur'])

const inputValue = defineModel()
const psdVal = ref('')
const popoverKeyboardVisible = ref(false)
function togglePopoverKeyboard(visible) {
  if (popoverKeyboardVisible.value !== visible) {
    if (visible) {
      focus()
    } else {
      blur()
    }
    popoverKeyboardVisible.value = visible
  }
}
const debounceTogglePopoverKeyboard = debounce(togglePopoverKeyboard, 50)
const customInputInner = ref(null)
const throttleScrollLeft = throttle(() => {
  nextTick(() => {
    customInputInner.value.scrollLeft = customInputInner.value.scrollWidth
  })
}, 100)
watch(inputValue, (newVal) => {
  psdVal.value = '*'.repeat(newVal.length)
  throttleScrollLeft()
})

const customInput = ref(null)
onClickOutside(
  customInput,
  (event) => {
    if (props.showKeyboard) {
      debounceTogglePopoverKeyboard(false)
      return
    }
    if (props.autoBlur) {
      blur()
    }
  },
  { capture: true }
)
const customPassword = ref(null)
onClickOutside(
  customPassword,
  (event) => {
    if (props.showKeyboard) {
      debounceTogglePopoverKeyboard(false)
      return
    }
    if (props.autoBlur) {
      blur()
    }
  },
  { capture: true }
)
const inputInnerHeight = computed(() => {
  if (props.type === 'textarea') {
    return `${props.rows * 22}px`
  }
  return '22px'
})
// 创建计算属性virtualRef
const virtualRef = computed(() => {
  if (props.type === 'password') {
    return customPassword.value
  }
  return customInput.value
})
// 是否聚焦
const isFocus = ref(false)
function focus() {
  isFocus.value = true
  throttleScrollLeft()
  emits('focus')
}
function blur() {
  isFocus.value = false
  emits('blur')
}
onMounted(() => {
  if (props.autoFocus) {
    focus()
  }
})

function clearValue() {
  inputValue.value = ''
}

function handleClickInput() {
  if (props.showKeyboard) {
    debounceTogglePopoverKeyboard(true)
    return
  }
  focus()
}
const isVisible = ref(false)
function showPassword() {
  isVisible.value = !isVisible.value
}
defineExpose({
  focus,
  blur
})
</script>

<template>
  <el-popover
    v-if="showKeyboard"
    :popper-class="['popover-keyboard', popperClass]"
    :visible="popoverKeyboardVisible"
    :show-arrow="false"
    width="auto"
    virtual-triggering
    :virtual-ref="virtualRef"
    :placement="placement"
    :offset="popoverOffset"
  >
    <div class="panel-keyboard">
      <div class="keyboard-container" @click="debounceTogglePopoverKeyboard(true)">
        <CustomKeyboard
          v-model="inputValue"
          :enable-languages="enableLanguages"
          :types="keyboardTypes"
          :number-layout="numberLayout"
        />
      </div>
      <!--点击关闭icon时不需要手动关闭popover，点击键盘和输入框以外的地方就会关闭-->
      <Icon icon-class="el-icon-close" class="icon-close" size="20px" />
    </div>
  </el-popover>
  <!--文本框-->
  <div
    v-if="props.type === 'text' || props.type === 'textarea'"
    ref="customInput"
    class="custom-input"
    :class="{ clearable, textarea: type === 'textarea' }"
    @click="handleClickInput"
  >
    <Icon v-if="icon" :icon-class="icon" class="mr-8" />
    <div
      ref="customInputInner"
      class="custom-input-inner hide-scroll-bar"
      :style="{ height: inputInnerHeight }"
    >
      <span v-if="inputValue" class="pb-1">{{ inputValue }}</span>
      <span class="input-value-blink" :class="{ focus: isFocus }" />
      <span v-if="!inputValue" class="placeholder line-1">{{ placeholder }}</span>
    </div>
    <div v-if="clearable && inputValue && isFocus">
      <div v-if="type === 'textarea'" class="button-clear" @click.stop="clearValue">清空</div>
      <Icon v-else icon-class="el-icon-close" class="icon-clear" @click.stop="clearValue" />
    </div>
  </div>
  <!--  密码框-->
  <div
    v-if="props.type === 'password'"
    ref="customPassword"
    class="custom-input"
    :class="{ clearable }"
    @click="handleClickInput"
  >
    <Icon v-if="icon" :icon-class="icon" class="mr-8" />
    <div ref="customInputInner" class="custom-input-inner hide-scroll-bar">
      <span v-if="inputValue && isVisible" class="pb-1">{{ inputValue }}</span>
      <span v-if="!isVisible" class="passwordMask">{{ psdVal }}</span>
      <span class="input-value-blink" :class="{ focus: isFocus }" />
      <span v-if="!inputValue" class="placeholder line-1">{{ placeholder }}</span>
    </div>
    <Icon
      v-if="inputValue && isFocus"
      :icon-class="isVisible ? 'eye-open' : 'eye-close'"
      class="icon-eye"
      @click="showPassword"
    />
    <Icon
      v-if="clearable && inputValue && isFocus"
      icon-class="el-icon-close"
      class="icon-clear"
      @click.stop="clearValue"
    />
  </div>
</template>

<style scoped lang="scss">
.custom-input {
  display: inline-flex;
  align-items: center;
  position: relative;
  background-color: rgba(255, 255, 255, 0.2);
  padding: 5px 10px;
  border-radius: $radius;
  width: 240px;
  box-sizing: border-box;
  font-size: 14px;
  overflow: hidden;

  .icon-container {
    flex-shrink: 0;
  }

  &.clearable {
    padding-right: 34px;
  }

  .custom-input-inner {
    flex-grow: 1;
    overflow-x: auto;
    color: #ffffff;
    height: 22px;
    line-height: 22px;
    padding: 0;
    box-sizing: border-box;

    .placeholder {
      color: rgba(255, 255, 255, 0.8);
    }
  }

  .input-value-blink {
    position: sticky;
    right: 0;
    flex-shrink: 0;
    padding-left: 2px;
    height: 1.2em;

    @keyframes blink {
      0%,
      100% {
        background-color: #fff;
      }
      50% {
        background-color: transparent;
      }
    }
    &.focus::after {
      position: absolute;
      right: 0;
      top: 50%;
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      transform: translateY(-50%);
      animation: blink 1.2s infinite steps(1, start);
    }
  }

  &.textarea {
    width: 100%;

    &.clearable {
      padding-right: 55px;
    }
  }

  .placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  .clickable-input {
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    height: 30px;
    line-height: 30px;
    z-index: 9;
  }

  .value {
    height: 30px;
    line-height: 30px;
    width: 100%;
    user-select: none;

    &.hide {
      padding: 0 12px;
      box-sizing: border-box;
      visibility: hidden;
    }
  }

  .icon-clear {
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.8);
    color: #333333;
    border-radius: $radius-full;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  .button-clear {
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 10px;
    background-color: rgba(255, 255, 255, 0.21);
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 2px;
  }
}
</style>

<style lang="scss">
.el-popover.el-popper.popover-keyboard {
  padding: 0;
  background-color: $background-color-2;
  border-color: rgba(255, 255, 255, 0.25);
}

.panel-keyboard {
  position: relative;
  .keyboard-container {
    padding: 12px;
  }

  .icon-close {
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.8);
    color: #333333;
    border-radius: $radius-full;
    position: absolute;
    right: -10px;
    top: -10px;
  }
}
</style>
